// The event set as data: which of the game's events become telemetry events, under which name, keeping which fields.
// Everything not listed is dropped, and only flat primitive values pass (strings capped), so nothing a rule doesn't
// name (a player's typed text, an object graph) can leave the device. A game may add derived fields per source event
// (e.g. `victim: "hero"` from an entity id) before the rules' `where` and `fields` read them.

/** A value a telemetry event may carry. */
export type Primitive = string | number | boolean | null;

/** One rule: when `event` fires (and `where` matches), record `name` with the listed fields. */
export interface EventRule {
  /** The rule's id (a row id in the game's table). */
  id: string;
  /** The source event's name (a kernel event, or one the game raises itself, e.g. a settings change). */
  event: string;
  /** The telemetry event's name. */
  name: string;
  /** The payload fields kept (after derivation). None: the event alone. */
  fields?: readonly string[];
  /** Only when every listed field equals the value (or one of the values). */
  where?: Readonly<Record<string, Primitive | readonly Primitive[]>>;
  /** At most once per session. */
  once?: boolean;
  /** At least this many seconds between two records of this rule. */
  throttleS?: number;
}

/** Adds fields to a source event's payload before the rules read it. */
export type Derive = (payload: unknown) => Record<string, Primitive>;

/** Longest string field kept, characters. */
export const MAX_STRING = 64;

/** A primitive kept as is, a long string cut, anything else dropped (undefined). */
export function clean(v: unknown): Primitive | undefined {
  if (v === null || typeof v === "boolean") return v;
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  if (typeof v === "string") return v.length > MAX_STRING ? v.slice(0, MAX_STRING) : v;
  return undefined;
}

/** Problems in an event set: duplicate ids, empty names, a `where` value that isn't a primitive. */
export function checkEventSet(rules: readonly EventRule[]): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  for (const r of rules) {
    if (ids.has(r.id)) issues.push(`rule ${r.id}: duplicate id`);
    ids.add(r.id);
    if (!r.event) issues.push(`rule ${r.id}: no source event`);
    if (!r.name) issues.push(`rule ${r.id}: no name`);
    for (const [k, v] of Object.entries(r.where ?? {})) {
      const vals = Array.isArray(v) ? v : [v];
      if (vals.some((x) => clean(x) === undefined)) issues.push(`rule ${r.id}: where.${k} is not a primitive`);
    }
  }
  return issues;
}

/** One telemetry event a rule made. */
export interface MatchedEvent {
  rule: string;
  name: string;
  data: Record<string, Primitive>;
}

/** The compiled event set: the source events it listens to, and what one payload becomes. */
export interface EventSet {
  /** Every distinct source event the rules name. */
  sources(): string[];
  /** The telemetry events a source event makes now (`nowS`: seconds since the session started, for throttles). */
  apply(event: string, payload: unknown, nowS: number): MatchedEvent[];
  /** Forget `once` and throttle history (a new session). */
  reset(): void;
}

/** Compile the rules. `derive` adds fields per source event. */
export function compileEventSet(rules: readonly EventRule[], derive: Readonly<Record<string, Derive>> = {}): EventSet {
  const bySource = new Map<string, EventRule[]>();
  for (const r of rules) bySource.set(r.event, [...(bySource.get(r.event) ?? []), r]);
  const fired = new Set<string>();
  const lastAt = new Map<string, number>();
  return {
    sources: () => [...bySource.keys()],
    apply(event, payload, nowS) {
      const list = bySource.get(event);
      if (!list) return [];
      const base = typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};
      const fields: Record<string, unknown> = { ...base, ...(derive[event]?.(payload) ?? {}) };
      const out: MatchedEvent[] = [];
      for (const r of list) {
        if (r.once && fired.has(r.id)) continue;
        if (r.throttleS !== undefined && lastAt.has(r.id) && nowS - lastAt.get(r.id)! < r.throttleS) continue;
        const ok = Object.entries(r.where ?? {}).every(([k, want]) => (Array.isArray(want) ? want : [want]).includes(clean(fields[k]) as Primitive));
        if (!ok) continue;
        const data: Record<string, Primitive> = {};
        for (const f of r.fields ?? []) {
          const v = clean(fields[f]);
          if (v !== undefined) data[f] = v;
        }
        fired.add(r.id);
        lastAt.set(r.id, nowS);
        out.push({ rule: r.id, name: r.name, data });
      }
      return out;
    },
    reset() {
      fired.clear();
      lastAt.clear();
    },
  };
}
